/* eslint-disable camelcase */
import { isMetaPixelEnabled } from './client';

export interface MetaPixelUserData {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  external_id?: string;
}

const STANDARD_META_EVENTS = new Set([
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
]);

const toContentIds = (contentIds: unknown): string[] | undefined => {
  if (contentIds == null) {
    return undefined;
  }

  const ids = (Array.isArray(contentIds) ? contentIds : [contentIds])
    .map((id) => String(id))
    .filter(Boolean);

  return ids.length > 0 ? ids : undefined;
};

const toNumericValue = (value: unknown): number | undefined => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const buildMetaEventData = (props: Record<string, unknown>): Record<string, unknown> => {
  const contentIds = toContentIds(props.content_ids);
  const value = toNumericValue(props.transaction_value ?? props.value);
  const numItems = toNumericValue(props.quantity);

  return {
    ...(contentIds && { content_ids: contentIds }),
    ...(props.content_name && { content_name: props.content_name }),
    ...(props.content_type && { content_type: props.content_type }),
    ...(props.category && { content_category: props.category }),
    ...(value !== undefined && { value }),
    ...(props.currency && { currency: props.currency }),
    ...(numItems !== undefined && { num_items: numItems }),
    ...(props.event_source_url && { event_source_url: props.event_source_url }),
  };
};

const buildMetaEventOptions = (props: Record<string, unknown>): Record<string, unknown> => ({
  ...(props.event_id && { eventID: String(props.event_id) }),
});

/** Meta ct: lowercase, no spaces or punctuation (advanced matching). */
export const normalizeMetaCity = (city: string): string =>
  city.toLowerCase().replace(/[^a-z0-9]/g, '');

const buildMetaPageViewData = (props: Record<string, unknown>): Record<string, unknown> => ({
  ...(props.event_source_url && { event_source_url: String(props.event_source_url) }),
});

const buildMetaRegistrationUserData = (props: Record<string, unknown>): MetaPixelUserData => {
  const email = props.form_email ?? props.email;
  const phone = props.form_phone ?? props.phone ?? props.user_phone_number;
  const city = props.city_name;

  return {
    ...(email && { em: String(email) }),
    ...(phone && { ph: String(phone) }),
    ...(props.first_name && { fn: String(props.first_name) }),
    ...(props.last_name && { ln: String(props.last_name) }),
    ...(city && { ct: normalizeMetaCity(String(city)) }),
  };
};

const buildMetaCompleteRegistrationData = (
  props: Record<string, unknown>
): Record<string, unknown> => {
  const value = toNumericValue(props.transaction_value ?? props.value);
  const hasMonetaryValue =
    value !== undefined && value > 0 && props.currency != null && props.currency !== '';

  return {
    ...(props.event_source_url && { event_source_url: String(props.event_source_url) }),
    ...(hasMonetaryValue && value !== undefined && { value }),
    ...(hasMonetaryValue && props.currency && { currency: String(props.currency) }),
  };
};

export const metaPixelTrack = (
  eventName: string,
  props: Record<string, unknown> = {},
  eventOptions: Record<string, unknown> = {}
) => {
  if (typeof window === 'undefined' || !isMetaPixelEnabled() || typeof window.fbq !== 'function') {
    return;
  }

  const eventData = buildMetaEventData(props);
  const options = {
    ...buildMetaEventOptions(props),
    ...eventOptions,
  };
  const hasOptions = Object.keys(options).length > 0;

  if (STANDARD_META_EVENTS.has(eventName)) {
    window.fbq('track', eventName, eventData, hasOptions ? options : undefined);
    return;
  }

  window.fbq('trackCustom', eventName, eventData, hasOptions ? options : undefined);
};

export const metaPixelPageView = (props: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined' || !isMetaPixelEnabled() || typeof window.fbq !== 'function') {
    return;
  }

  const eventData = buildMetaPageViewData(props);
  const options = buildMetaEventOptions(props);
  const hasOptions = Object.keys(options).length > 0;

  window.fbq('track', 'PageView', eventData, hasOptions ? options : undefined);
};

export const metaPixelSetUserData = (userData: MetaPixelUserData) => {
  if (
    typeof window === 'undefined' ||
    !isMetaPixelEnabled() ||
    typeof window.fbq !== 'function' ||
    Object.keys(userData).length === 0
  ) {
    return;
  }

  window.fbq('set', 'userData', userData as Record<string, unknown>);
};

export const metaPixelCompleteRegistration = (props: Record<string, unknown> = {}) => {
  const userData = buildMetaRegistrationUserData(props);
  metaPixelSetUserData(userData);

  const eventData = {
    ...buildMetaCompleteRegistrationData(props),
    ...(props.event_id && { event_id: props.event_id }),
  };

  metaPixelTrack('CompleteRegistration', eventData);
  // Custom event mirroring CompleteRegistration for marketing-side reporting/naming needs.
  metaPixelTrack('Account Created', eventData);
};
